import React, { useState, useEffect } from 'react';
import { 
  SearchIcon, 
  CreditCardIcon,
  PlusIcon,
  MinusIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshIcon,
  CogIcon
} from '@heroicons/react/outline';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from 'sonner';

const WalletManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeWallets, setActiveWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(true);
  
  // Modal states
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showDeductMoneyModal, setShowDeductMoneyModal] = useState(false);
  const [showUpdateLimitModal, setShowUpdateLimitModal] = useState(false);
  
  // Form states
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [newDailyLimit, setNewDailyLimit] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchActiveWallets();
  }, []);

  const fetchActiveWallets = async () => {
    try {
      setLoadingWallets(true);
      const response = await api.get('/admin/wallet/active-wallets');
      setActiveWallets(response.data.data || []);
    } catch (error) {
      console.error('Error fetching active wallets:', error);
    } finally {
      setLoadingWallets(false);
    }
  };

  const searchWallet = async (queryOverride = null) => {
    const query = queryOverride || searchQuery;
    
    if (!query.trim()) {
      setError('Please enter a user ID or email');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Update search query if override was provided
      if (queryOverride) {
        setSearchQuery(queryOverride);
      }
      
      const response = await api.get(`/admin/wallet/details/${query}`);
      setWalletData(response.data.data);
      setNewDailyLimit(response.data.data.daily_limit.toString());
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setError(error.response?.data?.detail || 'Failed to fetch wallet details');
      setWalletData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMoney = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!reason || reason.trim().length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return;
    }

    if (processing) return; // Prevent double submission

    try {
      setProcessing(true);
      
      // Direct manual credit - NO Razorpay payment required
      const response = await api.post('/admin/wallet/credit-manual', {
        user_id: walletData.user_id,
        amount: parseFloat(amount),
        reason: reason.trim()
      });

      if (response.data.success) {
        toast.success(`₹${amount} successfully added to ${walletData.user_name}'s wallet!`);
        setShowAddMoneyModal(false);
        setAmount('');
        setReason('');
        searchWallet(); // Refresh data
      } else {
        toast.error('Failed to credit wallet');
      }
    } catch (error) {
      console.error('Error crediting wallet:', error);
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeductMoney = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > walletData.balance) {
      toast.error('Amount exceeds current balance');
      return;
    }

    if (processing) return; // Prevent double submission

    try {
      setProcessing(true);
      const response = await api.post(`/admin/wallet/debit`, null, {
        params: {
          user_id: walletData.user_id,
          amount: parseFloat(amount),
          description: reason || 'Manual debit - Refund processed'
        }
      });

      toast.success(response.data.message || `Successfully deducted ₹${amount} from user wallet`);
      setShowDeductMoneyModal(false);
      setAmount('');
      setReason('');
      searchWallet(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deduct money');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateLimit = async () => {
    if (!newDailyLimit || parseFloat(newDailyLimit) <= 0) {
      toast.error('Please enter a valid daily limit');
      return;
    }

    if (processing) return; // Prevent double submission

    try {
      setProcessing(true);
      const response = await api.put(`/admin/wallet/update-limit/${walletData.user_id}`, null, {
        params: {
          daily_limit: parseFloat(newDailyLimit)
        }
      });

      toast.success(response.data.message || 'Daily limit updated successfully');
      setShowUpdateLimitModal(false);
      searchWallet(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limit');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = !walletData.wallet_enabled;
    const confirmMessage = `Are you sure you want to ${newStatus ? 'enable' : 'disable'} this wallet?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await api.put(`/admin/wallet/update-status/${walletData.user_id}`, null, {
        params: {
          is_active: newStatus
        }
      });

      toast.success(response.data.message || `Wallet ${newStatus ? 'enabled' : 'disabled'} successfully`);
      searchWallet(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <CreditCardIcon className="h-8 w-8 mr-2 text-orange-600" />
          Wallet Management
        </h1>
        <p className="text-gray-600 mt-1">Manage user wallets, balances, and limits</p>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Enter User ID or Email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchWallet()}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            onClick={searchWallet}
            disabled={loading}
            className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 flex items-center"
          >
            <SearchIcon className="h-5 w-5 mr-2" />
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Active Wallets List */}
      {!walletData && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Active Wallets</h2>
            <button
              onClick={fetchActiveWallets}
              className="text-orange-600 hover:text-orange-700 flex items-center text-sm"
            >
              <RefreshIcon className="h-4 w-4 mr-1" />
              Refresh
            </button>
          </div>

          {loadingWallets ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : activeWallets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCardIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>No active wallets found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">KYC Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeWallets.map((wallet) => (
                    <tr key={wallet.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {wallet.user_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {wallet.user_email}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                        ₹{formatCurrency(wallet.balance)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          wallet.kyc_status === 'verified' 
                            ? 'bg-green-100 text-green-800'
                            : wallet.kyc_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {wallet.kyc_status === 'verified' ? '✓ Verified' :
                           wallet.kyc_status === 'pending' ? '⏳ Pending' :
                           '⚠ Not Submitted'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {wallet.is_active ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            searchWallet(wallet.user_email);
                          }}
                          className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                        >
                          View Details →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Wallet Details */}
      {walletData && (
        <div className="space-y-6">
          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">User Information</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1 text-sm text-gray-900">{walletData.user_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{walletData.user_email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">User ID</label>
                <p className="mt-1 text-sm text-gray-900 font-mono text-xs">{walletData.user_id}</p>
              </div>
            </div>
          </div>

          {/* Wallet Balance Card */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-orange-100 text-sm font-medium">TOTAL WALLET BALANCE</p>
                <p className="text-4xl font-bold mt-2">₹{formatCurrency(walletData.balance)}</p>
                <p className="text-orange-100 text-sm mt-1">Available for transactions</p>
              </div>
              <CreditCardIcon className="h-16 w-16 text-orange-200 opacity-50" />
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowAddMoneyModal(true)}
                className="flex-1 bg-white text-orange-600 px-4 py-2 rounded-md hover:bg-orange-50 flex items-center justify-center font-medium"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Money
              </button>
              <button
                onClick={() => setShowDeductMoneyModal(true)}
                className="flex-1 bg-orange-700 text-white px-4 py-2 rounded-md hover:bg-orange-800 flex items-center justify-center font-medium"
              >
                <MinusIcon className="h-5 w-5 mr-2" />
                Deduct Money
              </button>
            </div>
          </div>

          {/* Wallet Settings Cards */}
          <div className="grid grid-cols-2 gap-6">
            {/* Daily Spending Limit */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Daily Spending Limit</h3>
                  <p className="text-sm text-gray-600">Maximum daily transaction limit</p>
                </div>
                <button
                  onClick={() => setShowUpdateLimitModal(true)}
                  className="text-orange-600 hover:text-orange-700"
                >
                  <CogIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-2xl font-bold text-gray-900">₹{formatCurrency(walletData.daily_limit)}</p>
                  <p className="text-sm text-gray-600">of ₹{formatCurrency(walletData.daily_limit)} remaining today</p>
                </div>
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${(walletData.daily_remaining / walletData.daily_limit) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">
                  Spent today: ₹{formatCurrency(walletData.daily_spent)}
                </p>
              </div>
            </div>

            {/* Wallet Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Wallet Status</h3>
                  <p className="text-sm text-gray-600">Current wallet state</p>
                </div>
                <button
                  onClick={handleToggleStatus}
                  className={`px-4 py-2 rounded-md font-medium ${
                    walletData.wallet_enabled
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title={`Toggle wallet ${walletData.wallet_enabled ? 'off' : 'on'}`}
                >
                  {walletData.wallet_enabled ? 'Disable Wallet' : 'Enable Wallet'}
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  {walletData.is_active ? (
                    <>
                      <CheckCircleIcon className="h-12 w-12 text-green-500" />
                      <div>
                        <p className="text-xl font-bold text-green-700">Active</p>
                        <p className="text-sm text-gray-600">Wallet is operational</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-12 w-12 text-red-500" />
                      <div>
                        <p className="text-xl font-bold text-red-700">Inactive</p>
                        <p className="text-sm text-gray-600">
                          {walletData.is_frozen ? 'Wallet is frozen' : 'Wallet is disabled'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                {/* KYC Status Badge */}
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-xs font-medium text-gray-600">KYC Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    walletData.kyc_status === 'verified' 
                      ? 'bg-green-100 text-green-800'
                      : walletData.kyc_status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : walletData.kyc_status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {walletData.kyc_status === 'verified' ? '✓ Verified' :
                     walletData.kyc_status === 'pending' ? '⏳ Pending' :
                     walletData.kyc_status === 'rejected' ? '✗ Rejected' :
                     '⚠ Not Submitted'}
                  </span>
                </div>
                
                {/* Wallet Enable/Disable Status */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">Wallet Setting:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    walletData.wallet_enabled 
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {walletData.wallet_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Max Balance: ₹{formatCurrency(walletData.max_balance)}
                </p>
                {!walletData.kyc_verified && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠ KYC not verified: the customer can pay with this balance, but cannot add money online
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Money Modal */}
      {showAddMoneyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto border w-full max-w-lg shadow-2xl rounded-2xl bg-white animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-2">
                    <PlusIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Credit User Wallet</h3>
                    <p className="text-sm text-orange-50 mt-0.5">Manual wallet adjustment</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddMoneyModal(false);
                    setAmount('');
                    setReason('');
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* User Info Card */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-orange-100 rounded-full p-2">
                    <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{walletData.user_name}</p>
                    <p className="text-xs text-gray-500">{walletData.user_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Current Balance</p>
                    <p className="text-lg font-bold text-gray-900">₹{formatCurrency(walletData.balance)}</p>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Credit Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-semibold text-lg">₹</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    min="10"
                    className="w-full border-2 border-gray-300 rounded-xl pl-10 pr-4 py-3.5 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">Minimum: ₹10</p>
              </div>
              
              {/* Reason Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for Credit <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Promotional credit, Service compensation, Loyalty reward..."
                  className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition resize-none"
                  rows="3"
                  required
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-gray-500">
                    {reason.length < 10 ? (
                      <span className="text-orange-600 font-medium">{10 - reason.length} more characters needed</span>
                    ) : (
                      <span className="text-orange-600 font-medium">✓ Reason provided</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">{reason.length} characters</p>
                </div>
              </div>
              
              {/* Summary Card */}
              {amount && parseFloat(amount) > 0 && reason.length >= 10 && (
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 rounded-full p-2 mt-0.5">
                      <CheckCircleIcon className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-semibold text-orange-900">Transaction Summary</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-orange-700">Current Balance:</span>
                          <span className="font-semibold text-orange-900">₹{formatCurrency(walletData.balance)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-orange-700">Credit Amount:</span>
                          <span className="font-semibold text-orange-900">+ ₹{formatCurrency(parseFloat(amount))}</span>
                        </div>
                        <div className="border-t border-orange-200 pt-1.5 mt-1.5"></div>
                        <div className="flex justify-between text-base">
                          <span className="font-semibold text-orange-800">New Balance:</span>
                          <span className="font-bold text-orange-900 text-lg">₹{formatCurrency((walletData?.balance || 0) + parseFloat(amount))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowAddMoneyModal(false);
                    setAmount('');
                    setReason('');
                  }}
                  className="px-6 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMoney}
                  disabled={!amount || parseFloat(amount) < 10 || !reason || reason.trim().length < 10 || processing}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed font-semibold transition flex items-center space-x-2 shadow-lg shadow-orange-500/30"
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-5 w-5" />
                      <span>Credit Wallet</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deduct Money Modal */}
      {showDeductMoneyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-2xl rounded-xl bg-white animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-red-100 rounded-full p-3 mr-3">
                  <MinusIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Deduct Money from Wallet</h3>
              </div>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (₹)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    max={walletData?.balance}
                    className="w-full border-2 border-gray-300 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Available balance: ₹{formatCurrency(walletData?.balance || 0)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reason</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for deducting money (optional)"
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition resize-none"
                  rows="3"
                />
              </div>
              
              {amount && parseFloat(amount) > 0 && (
                <div className={`border rounded-lg p-4 ${
                  parseFloat(amount) > (walletData?.balance || 0)
                    ? 'bg-red-50 border-red-200'
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  {parseFloat(amount) > (walletData?.balance || 0) ? (
                    <p className="text-sm text-red-800 font-medium">
                      ⚠ Insufficient balance! Amount exceeds available balance.
                    </p>
                  ) : (
                    <p className="text-sm text-orange-800">
                      <span className="font-semibold">New Balance:</span> ₹{formatCurrency((walletData?.balance || 0) - parseFloat(amount))}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeductMoneyModal(false);
                  setAmount('');
                  setReason('');
                }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeductMoney}
                disabled={!amount || parseFloat(amount) <= 0 || parseFloat(amount) > (walletData?.balance || 0) || processing}
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition flex items-center"
              >
                <MinusIcon className="h-5 w-5 mr-2" />
                {processing ? 'Processing...' : 'Deduct Money'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Limit Modal */}
      {showUpdateLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-2xl rounded-xl bg-white animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-orange-100 rounded-full p-3 mr-3">
                  <CogIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Update Daily Spending Limit</h3>
              </div>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Daily Limit (₹)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <input
                    type="number"
                    value={newDailyLimit}
                    onChange={(e) => setNewDailyLimit(e.target.value)}
                    placeholder="0.00"
                    className="w-full border-2 border-gray-300 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Current limit: ₹{formatCurrency(walletData?.daily_limit || 0)}
                </p>
              </div>
              
              {newDailyLimit && parseFloat(newDailyLimit) > 0 && parseFloat(newDailyLimit) !== walletData?.daily_limit && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Change:</span> {
                      parseFloat(newDailyLimit) > walletData?.daily_limit
                        ? `+₹${formatCurrency(parseFloat(newDailyLimit) - walletData?.daily_limit)} increase`
                        : `-₹${formatCurrency(walletData?.daily_limit - parseFloat(newDailyLimit))} decrease`
                    }
                  </p>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowUpdateLimitModal(false)}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateLimit}
                disabled={!newDailyLimit || parseFloat(newDailyLimit) <= 0 || processing}
                className="px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition flex items-center"
              >
                <CogIcon className="h-5 w-5 mr-2" />
                {processing ? 'Processing...' : 'Update Limit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
